// import { useEffect, useLayoutEffect, useRef, useState } from "react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  ProcessMapData,
  ProcessMapLayer,
  ProcessHotSpot,
  ProcessMapBookmark,
} from "./ProcessMap.types";
import {
  pan,
  zoom,
  getScale,
  setScale,
  resetScale,
} from "svg-pan-zoom-container";
import { AnimatePresence, motion } from "framer-motion";
import MemoChervonIcon from "../Components/ChervonIcon";
import CheckIcon from "../Components/CheckIcon";
import MemoCloseIcon from "../Components/CloseIcon";
import MemoShareIcon from "../Components/ShareIcon";
import MemoBookmarkIcon from "../Components/BookmarkIcon";
import MemoBookmarkedIcon from "../Components/BookmarkedIcon";
import interact from "interactjs";
import ShareModal from "../Components/ShareModal";
import MemoPrintIcon from "../Components/PrintIcon";

const LOCAL_STORAGE_KEY = "processMapBookmarks";

const listVariant = {
  hidden: {
    x: -10,
    opacity: 0,
  },
  visible: {
    x: 0,
    opacity: 1,
  },
};

const ProcessMapTool: React.FC<ProcessMapData> = ({
  id,
  title,
  mobileWarningMessage,
  content,
  printVersion,
  layers,
  hotSpots,
  backLink,
}) => {
  let isMobile = window.innerWidth < 750;
  const localState = localStorage.getItem(LOCAL_STORAGE_KEY) ?? "{}";
  let savedBookmarks = [];

  const Viewer = useRef(null);

  if (localState) {
    try {
      const parsed = JSON.parse(localState);

      if (parsed && typeof parsed[id] !== "undefined") {
        savedBookmarks = parsed[id];
      }
    } catch (e) {}
  }

  const [bookmarks, setBookmarks] =
    useState<ProcessMapBookmark[]>(savedBookmarks);

  const url = new URL(window.location.href);
  const urlHotspot = url.searchParams.get("hotspot");
  const activeHotspotFromURL = urlHotspot
    ? hotSpots.find((hotspot) => hotspot.urlSegment === urlHotspot)
    : null;

  const [activeHotspot, setActiveHotspot] = useState<ProcessHotSpot | null>(
    activeHotspotFromURL ?? null
  );
  const [activeLayer, setActiveLayer] = useState<ProcessMapLayer>(layers[0]);
  const [showSidebar, setShowSidebar] = useState<boolean>(true);
  const [showShareModal, setShowShareModal] = useState<boolean>(false);
  const zoomDelta = 0.1;

  useLayoutEffect(() => {
    isMobile = window.innerWidth < 750;
  });

  useEffect(() => {
    if (Viewer.current) {
      setTimeout(() => {
        // remove the loading class from #process-map
        const parent = Viewer.current.closest("#process-map");

        if (parent) {
          parent.classList.remove("loading");
        }
      });

      const observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          console.log("scale:", getScale(Viewer.current));
        });
      });

      observer.observe(Viewer.current.firstElementChild, {
        attributes: true,
        attributeFilter: ["transform"],
      });

      return () => {
        if (Viewer.current) {
          const parent = Viewer.current.closest("#process-map");

          if (parent) {
            parent.classList.add("loading");
          }

          observer.disconnect();
        }
      };
    }
  }, []);

  useEffect(() => {
    // update the url params with the current active hotspot
    const url = new URL(window.location.href);

    if (activeHotspot) {
      url.searchParams.set("hotspot", activeHotspot.urlSegment);
    } else {
      url.searchParams.delete("hotspot");
    }

    window.history.replaceState({}, "", url.toString());
  }, [activeHotspot]);

  useEffect(() => {
    const state = localStorage.getItem(LOCAL_STORAGE_KEY) ?? "{}";
    let parsed = {};
    try {
      parsed = JSON.parse(state);
    } catch (e) {
      parsed = {};
    }

    parsed[id] = bookmarks;
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(parsed));
  }, [bookmarks]);

  useEffect(() => {
    // add the 'hotspot' class to any IDs within the Viewer.current element if
    // we have any hotspots matching
    if (Viewer.current) {
      hotSpots.forEach((hotspot) => {
        const element = Viewer.current.querySelector(`[id="${hotspot.svgId}"`);

        if (element) {
          element.classList.add("hotspot");
        }
      });
      interact.addDocument(window.document, {
        events: { passive: false },
      });

      interact(Viewer.current)
        .gesturable({})
        .origin("self")
        .on("gesturemove", (event) => {
          console.log("gesture move", { event });
        })
        .on("gestureend", (event) => {
          console.log("gesture end", { event });
        })
        .draggable({
          inertia: true,
          listeners: {
            move(event) {
              if (isMobile) {
                let speedFactor = 1.77;
                pan(
                  Viewer.current,
                  event.delta.x * -1 * speedFactor,
                  event.delta.y * -1 * speedFactor
                );
              }
            },
          },
        });
    }
  }, [activeLayer, Viewer]);

  const isBookmarked =
    bookmarks.filter((bookmark) => bookmark.svgId === activeHotspot?.svgId)
      .length > 0;

  // remove or add 'active' class from any hot spots within the Viewer.current
  useEffect(() => {
    const hotSpots = document.querySelectorAll(".hotspot");

    hotSpots.forEach((hotspot) => {
      if (!activeHotspot || hotspot.id !== activeHotspot?.svgId) {
        hotspot.classList.remove("active");
      } else {
        hotspot.classList.add("active");

        hotspot.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "nearest",
        });
      }
    });
  }, [activeHotspot]);

  const windowWidth = window.innerWidth;

  const scaleOptions = {
    origin: {
      clientX: 0,
      clientY: 0,
    },
    maxScale: 3,
    minScale: 0.2,
  };

  return (
    <div className="process-map">
      <AnimatePresence>
        {showSidebar === false && (
          <motion.div
            animate={{ opacity: 1, x: 0, y: 0 }}
            initial={{ x: isMobile ? 0 : "-100%", y: isMobile ? "100%" : 0 }}
            exit={{
              opacity: 0.8,
              x: isMobile ? 0 : "-100%",
              y: isMobile ? "100%" : 0,
            }}
            transition={{ duration: 0.4 }}
            className="process-map__sidebar process-map__sidebar--mini"
          >
            <button onClick={() => setShowSidebar(true)}>
              {title} <MemoChervonIcon />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSidebar && (
          <motion.div
            className="process-map__sidebar process-map__sidebar--full"
            initial={{ x: isMobile ? 0 : "-100%", y: isMobile ? "100%" : 0 }}
            animate={{ x: 0, y: 0 }}
            exit={{ x: isMobile ? 0 : "-100%", y: isMobile ? "100%" : 0 }}
            transition={{ duration: 0.6 }}
          >
            <button
              className="process-map__sidebar-close"
              onClick={() => {
                setShowSidebar(false);
                setActiveHotspot(null);
              }}
            >
              <svg
                width="10"
                height="18"
                viewBox="0 0 10 18"
                fill="none"
                pointerEvents={"none"}
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.83375 15.4169C6.57588 15.4169 6.34666 15.3309 6.17475 15.159L0.673496 9.65775C0.301016 9.31393 0.301016 8.71223 0.673496 8.3684L6.17475 2.86715C6.51857 2.49467 7.12027 2.49467 7.4641 2.86715C7.83658 3.21098 7.83658 3.81268 7.4641 4.1565L2.62186 8.99875L7.4641 13.8696C7.83658 14.2135 7.83658 14.8152 7.4641 15.159C7.29219 15.3309 7.06297 15.4169 6.83375 15.4169Z"
                  fill="#2F3A49"
                />
              </svg>
            </button>

            <div className="process-map__sidebar-inner">
              <h1 className="h4">{title}</h1>

              {!activeHotspot && mobileWarningMessage && windowWidth < 750 && (
                <div className="block-alert block-alert--info block-alert--super-compact">
                  <p
                    dangerouslySetInnerHTML={{ __html: mobileWarningMessage }}
                  ></p>
                </div>
              )}

              {!activeHotspot && content && (
                <div dangerouslySetInnerHTML={{ __html: content }}></div>
              )}

              <hr />

              {layers.length > 1 && (
                <>
                  <h2 className="h6">Available pathways</h2>

                  <ul className="process-map__layer-toggles">
                    {layers.map((layer) => {
                      const isActiveLayer = activeLayer.title === layer.title;

                      return (
                        <li
                          key={layer.title}
                          className={isActiveLayer ? "active" : ""}
                        >
                          <button
                            onClick={() => {
                              setActiveLayer(layer);
                              resetScale(Viewer.current);
                              setScale(Viewer.current, 1, scaleOptions);

                              const illustrationSize =
                                Viewer.current.firstElementChild.getBoundingClientRect()
                                  .width;
                              const windowSize = window.innerWidth;

                              // if illustration is wider that the window then pan to the center of the illustration
                              if (illustrationSize > windowSize) {
                                pan(
                                  Viewer.current,
                                  (illustrationSize - windowSize) / 2,
                                  0
                                );
                              } else {
                                pan(Viewer.current, 0, 0);
                              }
                            }}
                          >
                            <span
                              className="process-map__layer-key"
                              style={{
                                backgroundColor: layer.keyColour || "#ffffff",
                              }}
                            ></span>

                            {layer.title}

                            {isActiveLayer && (
                              <div className="icon icon--check">
                                <CheckIcon />
                              </div>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>

                  <hr />
                </>
              )}

              {bookmarks.length > 0 && (
                <>
                  <h2 className="h6">Bookmarks</h2>

                  <ul className="process-map__bookmarks">
                    {bookmarks.map((bookmark) => (
                      <li key={bookmark.svgId}>
                        <button
                          onClick={() => {
                            // remove bookmark
                            setBookmarks(
                              bookmarks.filter(
                                (bookmark) => bookmark.svgId !== bookmark.svgId
                              )
                            );
                          }}
                        >
                          <MemoBookmarkedIcon />
                        </button>
                        <a
                          onClick={() => {
                            const hotspot = hotSpots.find(
                              (hotspot) => hotspot.svgId === bookmark.svgId
                            );

                            if (hotspot) {
                              setActiveHotspot(hotspot);
                            }
                          }}
                        >
                          {bookmark.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {printVersion && (
                <>
                  <hr />
                  <div className="process-map__sidebar-actions">
                    <button
                      className="process-map__sidebar-action"
                      onClick={() => {
                        window.location.href = printVersion;
                      }}
                    >
                      Print <MemoPrintIcon />
                    </button>
                  </div>
                </>
              )}

              {backLink && (
                <button
                  type="button"
                  onClick={() => (window.location.href = backLink)}
                  className="process-map__sidebar-bottom-action"
                >
                  Explore other scenarios
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeHotspot && (
          <motion.div
            className="process-map__sidebar process-map__sidebar--full process-map__sidebar--hotspot"
            initial={{ x: isMobile ? 0 : "-100%", y: isMobile ? "100%" : 0 }}
            animate={{ x: 0, y: 0 }}
            exit={{ x: isMobile ? 0 : "-100%", y: isMobile ? "100%" : 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="process-map__sidebar-inner">
              <button
                className="process-map__sidebar-hotspot-close"
                onClick={() => setActiveHotspot(null)}
              >
                <MemoCloseIcon />
              </button>

              {activeHotspot.tag && (
                <span
                  className="process-map__sidebar-tag"
                  style={{
                    backgroundColor:
                      activeHotspot.tagColor || "var(--section-feature-color)",
                  }}
                >
                  {activeHotspot.tag}
                </span>
              )}

              {activeHotspot.title && (
                <h2 className="h4">{activeHotspot.title}</h2>
              )}

              {activeHotspot.content ? (
                <div
                  className="process-map__sidebar-content"
                  dangerouslySetInnerHTML={{ __html: activeHotspot.content }}
                ></div>
              ) : (
                <hr />
              )}

              {activeHotspot.alert && (
                <div
                  className="block-alert block-alert--info block-alert--compact"
                  dangerouslySetInnerHTML={{ __html: activeHotspot.alert }}
                ></div>
              )}

              {activeHotspot.nextSteps &&
                activeHotspot.nextSteps.length > 0 && (
                  <div className="process-map__sidebar-navigation">
                    <ul>
                      {activeHotspot.nextSteps.map(
                        (nextStep, nextStepIndex) => {
                          const nextHotspot = hotSpots.find(
                            (hotspot) =>
                              hotspot.contentId === nextStep.nextStepId
                          );

                          if (!nextHotspot) {
                            return null;
                          }

                          return (
                            <motion.li
                              key={nextStepIndex}
                              variants={listVariant}
                            >
                              <button
                                className="process-map__sidebar-navigation-button"
                                onClick={() => setActiveHotspot(nextHotspot)}
                              >
                                {nextStep.icon ? (
                                  <div
                                    className="icon"
                                    dangerouslySetInnerHTML={{
                                      __html: nextStep.icon,
                                    }}
                                  ></div>
                                ) : (
                                  <div className="icon"></div>
                                )}
                                {nextStep.label}
                              </button>
                            </motion.li>
                          );
                        }
                      )}
                    </ul>
                    <hr />
                  </div>
                )}

              <div className="process-map__sidebar-actions">
                <button
                  className={`process-map__sidebar-action ${
                    isBookmarked ? "active" : ""
                  }`}
                  onClick={() => {
                    if (isBookmarked) {
                      setBookmarks(
                        bookmarks.filter(
                          (bookmark) => bookmark.svgId !== activeHotspot.svgId
                        )
                      );
                    } else {
                      setBookmarks([
                        ...bookmarks,
                        {
                          title: activeHotspot.title,
                          svgId: activeHotspot.svgId,
                          urlSegment: activeHotspot.urlSegment,
                        },
                      ]);
                    }
                  }}
                >
                  {isBookmarked ? "Bookmarked" : "Bookmark"}
                  {isBookmarked ? <MemoBookmarkedIcon /> : <MemoBookmarkIcon />}
                </button>

                <button
                  className="process-map__sidebar-action"
                  onClick={(e) => {
                    setShowShareModal(true);
                  }}
                >
                  Share
                  <MemoShareIcon />
                </button>

                <ShareModal
                  isOpen={showShareModal}
                  closeModal={() => setShowShareModal(false)}
                />

                {printVersion && (
                  <button
                    className="process-map__sidebar-action icon-only"
                    title="Print this page"
                    onClick={() => {
                      window.location.href = printVersion;
                    }}
                  >
                    <MemoPrintIcon />
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="process-map__viewer">
        {activeLayer && (
          <div
            className="process-map__viewer-svg"
            data-pan-on-drag
            ref={Viewer}
            dangerouslySetInnerHTML={{ __html: activeLayer.svgText }}
            onClick={(e) => {
              // if clicking on a hotspot or a child of the hotspot then open
              // the hotspot
              const target = e.target as HTMLElement;

              let hotspotNode = null;

              if (target.classList.contains("hotspot")) {
                hotspotNode = target;
              } else {
                hotspotNode = target.closest(".hotspot");
              }

              if (hotspotNode) {
                const hotspot = hotSpots.find(
                  (hotspot) => hotspot.svgId === hotspotNode.id
                );

                if (hotspot) {
                  setActiveHotspot(hotspot);
                }
              }
            }}
          ></div>
        )}

        <div className="process-map__viewer-controls">
          <button
            onClick={() =>
              setScale(
                Viewer.current,
                getScale(Viewer.current) + zoomDelta,
                scaleOptions
              )
            }
            title="Zoom in"
          >
            <svg
              width="12"
              height="13"
              viewBox="0 0 12 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.9584 6.16846C11.9584 6.68408 11.5287 7.11377 11.0417 7.11377H6.91675V11.2388C6.91675 11.7257 6.48706 12.1268 6.00008 12.1268C5.48446 12.1268 5.08342 11.7257 5.08342 11.2388V7.11377H0.958415C0.44279 7.11377 0.0417484 6.68408 0.0417484 6.16846C0.0417484 5.68148 0.44279 5.28044 0.958415 5.28044H5.08342V1.15544C5.08342 0.639811 5.48446 0.210123 6.00008 0.210123C6.48706 0.210123 6.91675 0.639811 6.91675 1.15544V5.28044H11.0417C11.5287 5.25179 11.9584 5.68148 11.9584 6.16846Z"
                fill="#2F3A49"
              />
            </svg>
          </button>

          <button
            onClick={() => {
              setScale(
                Viewer.current,
                getScale(Viewer.current) - zoomDelta,
                scaleOptions
              );
            }}
            title="Zoom out"
          >
            <svg
              width="12"
              height="3"
              viewBox="0 0 12 3"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.0417 2.08512H0.958415C0.44279 2.08512 0.0417484 1.68408 0.0417484 1.16846C0.0417484 0.681478 0.44279 0.25179 0.958415 0.25179H11.0417C11.5287 0.25179 11.9584 0.681478 11.9584 1.16846C11.9584 1.68408 11.5287 2.08512 11.0417 2.08512Z"
                fill="#2F3A49"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProcessMapTool;
